import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  MCP_FILTERABLE_FIELDS,
  type MCPServerListItem,
} from '@kirin/api-client/contracts/mcp';
import {
  MCP_FILTER_URL_KEYS,
  MCP_TYPE_OPTIONS,
  MCP_TRANSPORT_OPTIONS,
  MCP_STATUS_OPTIONS,
  MCP_RISK_OPTIONS,
} from './constants';
import {
  NameCell,
  VersionCell,
  PublisherCell,
  TypeCell,
  TransportCell,
  UsersCell,
  StatusCell,
  FirstSeenCell,
  LastSeenCell,
  ToolsCell,
  SourceCell,
  RiskCell,
} from './CellRenderers';
import {
  MCPUserFilterOption,
  MCPTypeFilterOption,
  MCPTransportFilterOption,
  MCPStatusFilterOption,
  MCPPublisherFilterOption,
  MCPVersionFilterOption,
  McpRiskFilterOption,
} from './FilterComponents';
import { fetchMCPFieldFilterOptions } from './api';
import { GatewayControlCell } from './GatewayControlCell';
import type { GatewayControlCallbacks } from './gatewayControlUtils';
import { McpEnforcementCell } from './McpEnforcementCell';
import { McpServerStatusBadge } from './McpServerStatusBadge';
import { queryKeys } from '@/hooks/api/queryKeys';
import type { MCPRiskLevel } from '@kirin/api-client/contracts/mcp';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

// KIR-5108: "one or more risk levels" — the backend accepts includes only.
const RiskOperators = [{ value: FilterOperators.INCLUDES, label: 'Includes' }];

/** "NEW" pill on a newly added column header (same look as the NEW tabs). */
const NEW_COLUMN_BADGE = (
  <span className="shrink-0 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-blue-700 dark:bg-blue-900 dark:text-blue-300">
    New
  </span>
);

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

/**
 * KIR-4482 Enforcement column wiring. Passed only while
 * `mcp_inventory_enforcement` is on.
 */
export interface McpEnforcementColumnOptions {
  canEdit: boolean;
  isPending: (configName: string) => boolean;
  onAllow: (row: MCPServerListItem) => void;
  onRequestBlock: (row: MCPServerListItem) => void;
  /** KIR-4701: set while a security group's choices are shown and edited. */
  group?: { name: string };
}

export function createMCPColumns(
  organizationId: string,
  onSelectServer?: (serverName: string) => void,
  gatewayControlEnabled = false,
  gatewayControl?: GatewayControlCallbacks,
  enforcement?: McpEnforcementColumnOptions,
  /** KIR-5108: show the AgentMesh Risk column (`agentmesh_mcp_risk`). */
  riskEnabled = false
): ColumnConfig<MCPServerListItem>[] {
  const columns: ColumnConfig<MCPServerListItem>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) =>
        enforcement ? (
          <div className="flex items-start gap-2">
            <NameCell row={row} />
            <McpServerStatusBadge row={row} />
          </div>
        ) : (
          <NameCell row={row} />
        ),
      width: 280,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'name', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'version',
      header: 'Version',
      // `versions` carries both sources as tagged entries (KIR-4844); the cell
      // headlines an installed one. The id stays `version` — it is the
      // persisted table-state key — and the backend filter spans both sources.
      accessor: 'versions',
      renderCell: ({ row }) => <VersionCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'version',
      sortKey: 'v',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.VERSION],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'version', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'version', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPVersionFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'publisher',
      header: 'Publisher',
      accessor: 'publisher',
      renderCell: ({ row }) => <PublisherCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'publisher',
      sortKey: 'pub',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.PUBLISHER],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'publisher', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'publisher', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPPublisherFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'type',
      renderCell: ({ row }) => <TypeCell row={row} />,
      width: 120,
      sortable: true,
      sortField: 'type',
      sortKey: 'ty',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TYPE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TYPE_OPTIONS,
        render: (option, isSelected) => (
          <MCPTypeFilterOption type={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'transport',
      header: 'Transport',
      accessor: 'transport',
      renderCell: ({ row }) => <TransportCell row={row} />,
      width: 200,
      sortable: true,
      sortField: 'transport',
      sortKey: 'tr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TRANSPORT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TRANSPORT_OPTIONS,
        render: (option, isSelected) => (
          <MCPTransportFilterOption
            transport={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'userCount',
      renderCell: ({ row }) => (
        <UsersCell row={row} onSelectServer={onSelectServer} />
      ),
      width: 120,
      sortable: true,
      sortField: 'userCount',
      sortKey: 'uc',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.USERS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'users', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'users', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPUserFilterOption
            userId={option.value}
            userName={(option.metadata?.name as string | null) ?? null}
            userEmail={(option.metadata?.email as string | null) ?? null}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'tools',
      header: 'Tools',
      accessor: 'tools',
      renderCell: ({ row }) => <ToolsCell row={row} />,
      width: 120,
      sortable: true,
      sortField: 'tools',
      sortKey: 'to',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TOOLS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'tools', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'tools', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'runningCount',
      renderCell: ({ row }) => <StatusCell row={row} />,
      width: 120,
      sortable: true,
      sortField: 'runningCount',
      sortKey: 'rc',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.STATUS],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_STATUS_OPTIONS,
        render: (option, isSelected) => (
          <MCPStatusFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First Seen',
      timestamp: true,
      accessor: 'firstSeenAt',
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      timestamp: true,
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.LAST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];

  // KIR-5108 AgentMesh Risk, right after Status (mockup 1).
  if (riskEnabled) {
    const statusIndex = columns.findIndex(c => c.id === 'status');
    columns.splice(statusIndex + 1, 0, {
      id: 'risk',
      header: 'Risk',
      headerBadge: NEW_COLUMN_BADGE,
      accessor: 'risk',
      renderCell: ({ row }) => (
        <RiskCell row={row} organizationId={organizationId} />
      ),
      width: 150,
      // The detail card's trigger must stay fully visible.
      clipOverflow: false,
      sortable: true,
      sortField: 'risk',
      sortKey: 'rk',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: RiskOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.RISK],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_RISK_OPTIONS,
        render: (option, isSelected) => (
          <McpRiskFilterOption
            level={option.value as MCPRiskLevel}
            isSelected={isSelected}
          />
        ),
      },
    });
  }

  // Source is the ONLY column that distinguishes a gateway row from an endpoint
  // one, and the backend union returns gateway rows with no flag of its own
  // (see the merge-order note on `includeGateway` in
  // user-mcp-configurations.repository.ts). Until #2473 lands and turns this
  // flag on, nothing may write to `gateway_mcp_servers` — otherwise gateway
  // rows arrive here and render as ordinary endpoint rows.
  if (gatewayControlEnabled) {
    columns.splice(1, 0, {
      id: 'source',
      header: 'Source',
      renderCell: ({ row }) => <SourceCell row={row} />,
      width: 140,
    });
  }

  if (gatewayControlEnabled && gatewayControl) {
    columns.push({
      id: 'gatewayControl',
      header: 'Gateway Control',
      // The block toggle and sync badge must stay fully visible.
      clipOverflow: false,
      renderCell: ({ row }) => (
        <GatewayControlCell
          row={row}
          appliedPolicyVersion={gatewayControl.appliedPolicyVersion}
          onAllowServer={gatewayControl.onAllowServer}
          onRequestBlockConfirmation={gatewayControl.onRequestBlockConfirmation}
        />
      ),
      width: 220,
    });
  }

  if (enforcement) {
    columns.push({
      id: 'enforcement',
      header: enforcement.group
        ? `Enforcement (${enforcement.group.name})`
        : 'Enforcement',
      // The switch and its sub-label must stay fully visible.
      clipOverflow: false,
      renderCell: ({ row }) => (
        <McpEnforcementCell
          row={row}
          canEdit={enforcement.canEdit}
          pending={enforcement.isPending(row.name)}
          onAllow={enforcement.onAllow}
          onRequestBlock={enforcement.onRequestBlock}
          group={enforcement.group}
        />
      ),
      width: enforcement.group ? 280 : 230,
    });
  }

  return columns;
}
